import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Download, Plus, Share, X } from "lucide-react";
import { MainLogo } from "../assets/main_logo";
import { usePwaInstall } from "../hooks/use-pwa-install";
import posthog from "posthog-js";

type VisibilityState = "entering" | "visible" | "exiting" | "hidden";

const MAIN_ROUTE_INSTALL_PROMPT_DELAY_MS = 20_000;

/**
 * Fixed bottom banner that promotes PWA installation.
 * Slides up from the bottom on mount, slides down on dismiss.
 */
export const PwaInstallBanner = () => {
  const [isIosHelpOpen, setIsIosHelpOpen] = useState(false);
  const [visibility, setVisibility] = useState<VisibilityState>("entering");
  const [hasStayedOnMainRouteForDelay, setHasStayedOnMainRouteForDelay] = useState(false);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const {
    dismissInstallBanner,
    handleInstall,
    installPwa,
    canPromptInstall,
    isHandleInstallLoading,
    isInstallBannerVisible,
    isInstalling,
    platform,
  } = usePwaInstall();
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const isMainRoute = location.pathname === "/store";

  useEffect(() => {
    setHasStayedOnMainRouteForDelay(false);

    if (!isMainRoute) {
      return;
    }

    const delayTimeout = setTimeout(() => {
      setHasStayedOnMainRouteForDelay(true);
    }, MAIN_ROUTE_INSTALL_PROMPT_DELAY_MS);

    return () => clearTimeout(delayTimeout);
  }, [isMainRoute]);

  useEffect(() => {
    if (visibility !== "entering") return;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setVisibility("visible");
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [visibility]);

  if (!isMobile || !isInstallBannerVisible || visibility === "hidden" || !hasStayedOnMainRouteForDelay) {
    return null;
  }

  const handleDismiss = () => {
    if (visibility !== "visible") return;
    dismissInstallBanner();
    setVisibility("exiting");
    transitionTimeoutRef.current = setTimeout(() => {
      setVisibility("hidden");
    }, 400);
  };

  const handlePrimaryAction = async (): Promise<void> => {
    posthog.capture("Install APP", { platform });
    if (platform === "ios") {
      setIsIosHelpOpen(true);
      return;
    }
    if (platform === "android") {
      await handleInstall();
      return;
    }
    if (canPromptInstall) {
      await installPwa();
    }
  };

  const isExiting = visibility === "exiting";

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${isExiting ? "translate-y-full" : "translate-y-0"}`}
      >
        <div className="mx-auto max-w-2xl px-2 pb-4">
          <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-2 py-3 shadow-lg">
            <Button
              aria-label="Cerrar banner de instalación"
              onClick={handleDismiss}
              size="icon-sm"
              type="button"
              variant="ghost"
              className="shrink-0"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>

            <MainLogo classname="size-9 shrink-0" />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-5 text-foreground truncate">
                Accede más rápido
              </p>
              <p className="mt-0.5 text-xs leading-4 text-muted-foreground truncate">
                {platform === "ios"
                  ? "Agrega a tu pantalla de inicio"
                  : "Instála en tu teléfono"}
              </p>
            </div>

            <Button
              disabled={platform === "android" && isHandleInstallLoading}
              onClick={() => { void handlePrimaryAction(); }}
              size="sm"
              type="button"
              variant="default"
              className="shrink-0"
            >
              <Download className="h-4 w-4 mr-1" />
              {platform === "ios"
                ? "Instalar"
                : isHandleInstallLoading || isInstalling
                  ? "Abriendo"
                  : "Instalar"}
            </Button>
          </div>
        </div>
      </div>

      <Drawer open={isIosHelpOpen} onOpenChange={setIsIosHelpOpen}>
        <DrawerContent className="mx-auto w-full max-w-md rounded-t-3xl border-border/60">
          <DrawerHeader className="relative px-5 pb-3 pt-5 text-left">
            <Button
              aria-label="Cerrar ayuda de instalación"
              className="absolute right-4 top-4 h-7 w-7 rounded-full border-0 bg-muted/70 p-0 text-muted-foreground hover:bg-muted"
              onClick={() => setIsIosHelpOpen(false)}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
            <DrawerTitle className="max-w-[85%] text-base leading-5">
              Instala la app en tu iPhone o iPad
            </DrawerTitle>
            <DrawerDescription>
              Safari requiere agregarla manualmente desde el menú de compartir.
            </DrawerDescription>
          </DrawerHeader>
          <div className="space-y-2 px-5 pb-5 text-sm text-foreground/90">
            <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/30 px-3 py-3">
              <span className="w-5 shrink-0 text-right font-semibold text-foreground">1.</span>
              <span className="flex-1 leading-5">Toca</span>
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-500">
                <Share className="h-4 w-4" />
              </span>
              <span className="text-xs text-muted-foreground">en la barra de Safari</span>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/30 px-3 py-3">
              <span className="w-5 shrink-0 text-right font-semibold text-foreground">2.</span>
              <span className="flex-1 leading-5">Selecciona Agregar a pantalla de inicio</span>
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-foreground">
                <Plus className="h-4 w-4" />
              </span>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/30 px-3 py-3">
              <span className="w-5 shrink-0 text-right font-semibold text-foreground">3.</span>
              <span className="flex-1 leading-5">Busca CarriApp en tu pantalla de inicio</span>
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center">
                <MainLogo classname="h-5 w-5" />
              </span>
            </div>
            <Button className="mt-3 w-full" onClick={() => setIsIosHelpOpen(false)} type="button">
              Entendido
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
